import React, { useEffect, useRef } from 'react';
import { BRAND_COLORS } from './constants';

// Version 2: Dynamic Pipeline Network Background
export const PipelineBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Configuration state
        let config = {
            nodeCount: 10,
            connectionDensity: 70,
            animationSpeed: 1,
            scale: 100,
            globalOpacity: 80,
            globalScale: 100
        };

        // Listen for configuration changes
        const handleConfigChange = (event: CustomEvent) => {
            const newConfig = event.detail;
            config = {
                nodeCount: newConfig.v2NodeCount || config.nodeCount,
                connectionDensity: newConfig.v2ConnectionDensity || config.connectionDensity,
                animationSpeed: newConfig.v2AnimationSpeed || config.animationSpeed,
                scale: newConfig.v2Scale || config.scale,
                globalOpacity: newConfig.globalOpacity || config.globalOpacity,
                globalScale: newConfig.globalScale || config.globalScale
            };
            // Reinitialize network when config changes
            initializeNetwork();
        };

        window.addEventListener('backgroundConfigChange', handleConfigChange as EventListener);

        // Dynamic pipeline network system
        interface Node {
            x: number;
            y: number;
            pulse: number;
            id: string;
            connections: string[];
            type: 'source' | 'process' | 'sink';
        }

        interface Connection {
            from: string;
            to: string;
            progress: number;
            active: boolean;
        }

        let nodes: Map<string, Node> = new Map();
        let connections: Connection[] = [];
        let lastNetworkUpdate = 0;

        const generateNodeId = () => Math.random().toString(36).slice(2, 11);

        const createNode = (x: number, y: number, type: 'source' | 'process' | 'sink' = 'process'): Node => ({
            x,
            y,
            pulse: Math.random() * Math.PI * 2,
            id: generateNodeId(),
            connections: [],
            type
        });

        const generateConnections = () => {
            connections = [];
            const nodeArray = Array.from(nodes.values());
            const densityFactor = config.connectionDensity / 100;

            // Connect sources to processes
            const sources = nodeArray.filter(n => n.type === 'source');
            const processes = nodeArray.filter(n => n.type === 'process');
            const sinks = nodeArray.filter(n => n.type === 'sink');

            sources.forEach(source => {
                const connectTo = processes.filter(p => Math.random() < densityFactor);
                connectTo.forEach(target => {
                    connections.push({
                        from: source.id,
                        to: target.id,
                        progress: Math.random(),
                        active: Math.random() < 0.8
                    });
                });
            });

            // Connect processes to sinks
            processes.forEach(process => {
                if (Math.random() < densityFactor * 0.8) {
                    const target = sinks[Math.floor(Math.random() * sinks.length)];
                    connections.push({
                        from: process.id,
                        to: target.id,
                        progress: Math.random(),
                        active: Math.random() < 0.8
                    });
                }
            });

            // Some process-to-process connections
            processes.forEach(process => {
                if (Math.random() < densityFactor * 0.4) {
                    const target = processes[Math.floor(Math.random() * processes.length)];
                    if (target.id !== process.id) {
                        connections.push({
                            from: process.id,
                            to: target.id,
                            progress: Math.random(),
                            active: Math.random() < 0.6
                        });
                    }
                }
            });
        };

        const initializeNetwork = () => {
            nodes.clear();
            connections = [];

            // Create initial seed nodes based on config
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const scaleFactor = (config.scale * config.globalScale) / 10000;
            const nodeSpacing = 100 * scaleFactor;

            const sourceCount = Math.max(1, Math.floor(config.nodeCount * 0.3));
            const processCount = Math.max(2, Math.floor(config.nodeCount * 0.5));
            const sinkCount = Math.max(1, Math.floor(config.nodeCount * 0.2));

            // Source nodes (left side)
            for (let i = 0; i < sourceCount; i++) {
                const node = createNode(
                    centerX - 300 * scaleFactor + Math.random() * nodeSpacing,
                    centerY - 150 * scaleFactor + i * (300 * scaleFactor / sourceCount) + Math.random() * 50 * scaleFactor,
                    'source'
                );
                nodes.set(node.id, node);
            }

            // Process nodes (center area - distributed across canvas)
            for (let i = 0; i < processCount; i++) {
                const node = createNode(
                    centerX - 200 * scaleFactor + Math.random() * 400 * scaleFactor,
                    centerY - 250 * scaleFactor + Math.random() * 500 * scaleFactor,
                    'process'
                );
                nodes.set(node.id, node);
            }

            // Sink nodes (right side)
            for (let i = 0; i < sinkCount; i++) {
                const node = createNode(
                    centerX + 200 * scaleFactor + Math.random() * nodeSpacing,
                    centerY - 150 * scaleFactor + i * (300 * scaleFactor / sinkCount) + Math.random() * 50 * scaleFactor,
                    'sink'
                );
                nodes.set(node.id, node);
            }

            generateConnections();
        };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initializeNetwork();
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        const growNetwork = () => {
            // Occasionally add new nodes and connections
            if (Math.random() < 0.02 && nodes.size < 20) {
                const existingNodes = Array.from(nodes.values());
                const randomExisting = existingNodes[Math.floor(Math.random() * existingNodes.length)];

                // Create new node near an existing one
                const newNode = createNode(
                    randomExisting.x + (Math.random() - 0.5) * 200,
                    randomExisting.y + (Math.random() - 0.5) * 200,
                    Math.random() < 0.7 ? 'process' : (Math.random() < 0.5 ? 'source' : 'sink')
                );

                // Keep within bounds
                newNode.x = Math.max(50, Math.min(canvas.width - 50, newNode.x));
                newNode.y = Math.max(50, Math.min(canvas.height - 50, newNode.y));

                nodes.set(newNode.id, newNode);

                // Connect to some existing nodes
                existingNodes.slice(0, 3).forEach(existing => {
                    if (Math.random() < 0.5) {
                        connections.push({
                            from: existing.id,
                            to: newNode.id,
                            progress: 0,
                            active: Math.random() < 0.8
                        });
                    }
                });
            }
        };

        let animationFrameId: number;

        const animate = (now: number) => {
            ctx.fillStyle = BRAND_COLORS.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const globalOpacity = config.globalOpacity / 100;
            const opacity = globalOpacity * 0.8; // Make it subtle for background
            const scaleFactor = (config.scale * config.globalScale) / 10000;

            // Periodically regenerate parts of the network
            if (now - lastNetworkUpdate > 5000 / config.animationSpeed) {
                growNetwork();
                lastNetworkUpdate = now;
            }

            // Draw connections
            connections.forEach(conn => {
                const fromNode = nodes.get(conn.from);
                const toNode = nodes.get(conn.to);

                if (!fromNode || !toNode) return;

                // Draw connection line
                const opacityHex = Math.floor(opacity * (conn.active ? 150 : 50)).toString(16).padStart(2, '0');
                ctx.strokeStyle = `${BRAND_COLORS.secondary}${opacityHex}`;
                ctx.lineWidth = (conn.active ? 3 : 1) * scaleFactor;
                ctx.beginPath();
                ctx.moveTo(fromNode.x, fromNode.y);
                ctx.lineTo(toNode.x, toNode.y);
                ctx.stroke();

                // Draw moving data packet on active connections
                if (conn.active) {
                    const progress = conn.progress;
                    const x = fromNode.x + (toNode.x - fromNode.x) * progress;
                    const y = fromNode.y + (toNode.y - fromNode.y) * progress;

                    ctx.fillStyle = `${BRAND_COLORS.primary}${Math.floor(opacity * 255).toString(16).padStart(2, '0')}`;
                    ctx.beginPath();
                    ctx.arc(x, y, 5 * scaleFactor, 0, Math.PI * 2);
                    ctx.fill();

                    // Update progress with animation speed
                    conn.progress += 0.008 * config.animationSpeed;
                    if (conn.progress > 1) {
                        conn.progress = 0;
                        // Randomly deactivate/reactivate
                        if (Math.random() < 0.1) {
                            conn.active = !conn.active;
                        }
                    }
                }
            });

            // Draw nodes
            nodes.forEach(node => {
                const pulseRadius = (15 + Math.sin(node.pulse) * 4) * scaleFactor;

                // Node type colors
                let nodeColor = BRAND_COLORS.primary;
                let pulseColor = BRAND_COLORS.secondary;

                if (node.type === 'source') {
                    nodeColor = BRAND_COLORS.secondary;
                    pulseColor = BRAND_COLORS.primary;
                } else if (node.type === 'sink') {
                    nodeColor = '#2D3748';
                    pulseColor = BRAND_COLORS.secondary;
                }

                // Outer pulse
                ctx.fillStyle = `${pulseColor}${Math.floor(opacity * 100).toString(16).padStart(2, '0')}`;
                ctx.beginPath();
                ctx.arc(node.x, node.y, pulseRadius, 0, Math.PI * 2);
                ctx.fill();

                // Inner node
                ctx.fillStyle = `${nodeColor}${Math.floor(opacity * 200).toString(16).padStart(2, '0')}`;
                ctx.beginPath();
                ctx.arc(node.x, node.y, 10 * scaleFactor, 0, Math.PI * 2);
                ctx.fill();

                node.pulse += 0.03 * config.animationSpeed;
            });

            animationFrameId = requestAnimationFrame(animate);
        };

        initializeNetwork();
        animationFrameId = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('backgroundConfigChange', handleConfigChange as EventListener);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0"
            style={{ background: BRAND_COLORS.background }}
        />
    );
};
